import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Elements,
  useElements,
  useStripe,
  AddressElement,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { type PaymentMethodCreateParams, Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { Button } from 'app-zephyr-components/Button';
import { SnackBarAlertComponent } from 'app-zephyr-components/SnackBarAlert';
import { envValue } from 'app-zephyr-environment';
import { CheckmarkIcon } from 'app-zephyr-icons/Checkmark';
import { HTMLAttributes, useCallback, useState } from 'react';
import type { AttachPaymentMethodMutationResult, AttachPaymentMethodParams, StripePaymentMethodDto } from 'ze-sdk';
import { Checkbox, FormControlLabel } from '@mui/material';

function StripeAddCardForm({
  onSubmitValidData,
  setAsDefaultLocked,
  onSuccess,
  className,
  paymentMethod,
  children,
  update = false,
  ...rest
}: HTMLAttributes<HTMLFormElement> & {
  onSubmitValidData: (data: AttachPaymentMethodParams) => Promise<AttachPaymentMethodMutationResult>;
  setAsDefaultLocked?: boolean;
  onSuccess?: () => void;
  paymentMethod?: StripePaymentMethodDto;
  update?: boolean;
}) {
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [isDefaultPaymentMethod, setIsDefaultPaymentMethod] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const stripe = useStripe();
  const elements = useElements();

  const addPaymentMethod = async (
    stripInstance: Stripe,
    {
      card,
      billingDetails,
    }: { card: StripeCardNumberElement; billingDetails: PaymentMethodCreateParams.BillingDetails },
  ) => {
    const results = await stripInstance.createPaymentMethod({
      type: 'card',
      card,
      billing_details: billingDetails,
    });
    return results;
  };

  const onAddPaymentMethod = useCallback(async () => {
    setIsSubmitting(true);
    if (!elements) {
      setSnackbarOpen(true);
      setIsSubmitting(false);
      return;
    }
    const cardNumber = elements.getElement('cardNumber');
    const cardExpiry = elements.getElement('cardExpiry');
    const cardCvc = elements.getElement('cardCvc');
    const cardAddressElement = elements.getElement('address');
    if (!stripe || !cardNumber || !cardExpiry || !cardCvc || !cardAddressElement) {
      setSnackbarOpen(true);
      setIsSubmitting(false);
      return;
    }
    const addressValue = await cardAddressElement.getValue();
    const address = addressValue.value.address;
    if (!address.city || !address.country || !address.line1 || !address.postal_code) {
      setSnackbarOpen(true);
      setIsSubmitting(false);
      return;
    }

    const { paymentMethod, error } = await addPaymentMethod(stripe, {
      card: cardNumber,
      billingDetails: { address },
    });

    if (error) {
      setIsSubmitting(false);
      setSnackbarOpen(true);
      return;
    }

    try {
      await onSubmitValidData({
        paymentMethodId: paymentMethod.id,
        useAsDefault: isDefaultPaymentMethod,
      });
      onSuccess?.();
    } catch {
      setSnackbarOpen(true);
    }
  }, [elements, onSubmitValidData, onSuccess, isDefaultPaymentMethod, stripe]);

  const style = {
    base: {
      // input: { color: '#DDDDDD', backgroundColor: 'red' },
      backgroundColor: '#30313d', // tokens['gray-950']
      color: '#fff', // tokens['gray-25']
      fontFamily: 'Inter',

      fontSize: '16px',
      fontWeight: '400',
      fontSmoothing: 'antialiased',
      lineHeight: '3',
      padding: '14px',
      // borderRadius: '8px', // theme.borderRadius.lg
      ':-webkit-autofill': {
        color: '#FFFFFF',
      },
      '::placeholder': {
        color: '#646464', // tokens['gray-400']
      },
    },
    invalid: {
      color: '#F97066', // tokens['error-400']
      iconColor: '#F97066',
    },
    complete: {
      color: '#47CD89', // tokens['success-400']
    },
  };

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          padding: 0.1,
          pb: 3,
        }}
      >
        <Box>
          <CardNumberElement options={{ style: style, placeholder: 'Card Number' }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <CardExpiryElement options={{ style: style }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <CardCvcElement options={{ style: style }} />
          </Box>
        </Box>
      </Box>
      <AddressElement
        options={{
          mode: 'billing',
          autocomplete: { mode: 'automatic' },
        }}
      />
      <FormControlLabel
        label="Set as default"
        disabled={setAsDefaultLocked}
        control={
          <Checkbox
            checked={isDefaultPaymentMethod}
            onChange={(e) => {
              setIsDefaultPaymentMethod(e.target.checked);
            }}
          />
        }
      />

      <Box sx={{ width: '100%' }}>
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          type="button"
          onClick={() => void onAddPaymentMethod()}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size="16px" />
          ) : (
            <>
              <CheckmarkIcon style={{ marginRight: '8px' }} color="#54C752" /> Save New Payment Method
            </>
          )}
        </Button>
      </Box>
      <SnackBarAlertComponent
        open={snackbarOpen}
        onClose={() => {
          setSnackbarOpen(false);
        }}
        message="Error when adding payment data. Check the entered data again or enter another one"
        alertColor="error"
      />
    </div>
  );
}

function StripePaymentMethodForm(
  props: HTMLAttributes<HTMLFormElement> & {
    onSubmitValidData: (data: AttachPaymentMethodParams) => Promise<AttachPaymentMethodMutationResult>;
    setAsDefaultLocked?: boolean;
    onSuccess?: () => void;
    paymentMethod?: StripePaymentMethodDto;
    update?: boolean;
  },
) {
  // this is a workaround since importing this as the top of the file causes a request to be sent to stripe when the app is loaded
  const module = import('@stripe/stripe-js');
  return (
    <Elements
      stripe={module.then((m) => m.loadStripe(envValue.value.stripePkKey))}
      options={{
        appearance: { theme: 'night' },
      }}
    >
      <StripeAddCardForm {...props} />
    </Elements>
  );
}

export { StripePaymentMethodForm };
